import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { Navigate } from 'react-router'

import { isSignedIn, loadTokens } from '@/shared/api'

/**
 * Guards the authenticated shell. Token storage is async (it is a Keychain
 * read under Capacitor), so the first paint waits on it rather than flashing
 * the splash at a signed-in user.
 *
 * **Holding tokens is not being signed in.** Both halves of a two-step login
 * store a real access token that opens only the endpoint collecting the factor
 * still owed, so a guard that asked "are there tokens?" let a half-finished
 * login render the whole app, where every request then 401s. It asks for
 * `active` instead.
 */
export function RequireSession({ children }: { children: ReactNode }) {
  const [state, setState] = useState<'checking' | 'in' | 'out'>('checking')

  useEffect(() => {
    let active = true
    void loadTokens().then((tokens) => {
      if (active) setState(isSignedIn(tokens) ? 'in' : 'out')
    })
    return () => {
      active = false
    }
  }, [])

  if (state === 'checking') return <div className="min-h-dvh bg-bg" />
  if (state === 'out') return <Navigate to="/welcome" replace />
  return <>{children}</>
}
